/**
 * As cinco plantas do jardim, transcritas de `plant_type.dart`.
 *
 * O `id` é o que fica gravado em `garden_stats`, e por isso continua em inglês
 * como o Flutter escreveu. O nome e as descrições de fase também vieram de lá
 * palavra por palavra: os dois apps leem o mesmo documento, e uma planta
 * traduzida só aqui viraria outra planta do outro lado.
 *
 * As cinco flores ficam emoji, e a casa não as troca por traço como fez com os
 * corredores do mercado: lá o emoji rotulava um cabeçalho, e aqui ele **é** a
 * planta. Elas aparecem uma ao lado da outra na hora de escolher, e o que a
 * escolha pede é justamente distinguir lírio de rosa de girassol num relance —
 * cinco flores em traço monocromático de 24 px viram cinco borrões parecidos.
 */

export interface Planta {
  id: string;
  displayName: string;
  emoji: string;
  /** Uma frase por fase, de 0 (vaso vazio) a 5 (florida). */
  stageDescriptions: string[];
  /** O mês (1-12) em que esta planta ganha uma fase de presente. */
  seasonalBonusMonth?: number;
  seasonalBonusName?: string;
}

export const PLANTAS: readonly Planta[] = [
  {
    id: 'lily',
    displayName: 'Lily',
    emoji: '🌸',
    stageDescriptions: [
      'Empty pot',
      'A tiny sprout',
      'Growing bud',
      'Opening bloom',
      'Half bloom',
      'Full bloom',
    ],
  },
  {
    id: 'rose',
    displayName: 'Rose',
    emoji: '🌹',
    stageDescriptions: [
      'Empty pot',
      'A tiny sprout',
      'Thorny stem',
      'Rose bud',
      'Opening rose',
      'Full rose',
    ],
    seasonalBonusMonth: 2, // Fevereiro (Namorados)
    seasonalBonusName: 'Floração dos Namorados',
  },
  {
    id: 'sunflower',
    displayName: 'Sunflower',
    emoji: '🌻',
    stageDescriptions: [
      'Empty pot',
      'A tiny sprout',
      'Tall stem',
      'Budding head',
      'Opening petals',
      'Full sunflower',
    ],
    seasonalBonusMonth: 7, // Julho (verão de lá)
    seasonalBonusName: 'Summer Radiance',
  },
  {
    id: 'tulip',
    displayName: 'Tulip',
    emoji: '🌷',
    stageDescriptions: [
      'Empty pot',
      'A tiny sprout',
      'Slender stem',
      'Tulip bud',
      'Opening tulip',
      'Full tulip',
    ],
    seasonalBonusMonth: 4, // Abril
    seasonalBonusName: 'Spring Awakening',
  },
  {
    id: 'sakura',
    displayName: 'Sakura',
    emoji: '🌸',
    stageDescriptions: [
      'Empty pot',
      'A tiny sprout',
      'Branching tree',
      'Cherry buds',
      'Opening blossoms',
      'Full sakura',
    ],
    seasonalBonusMonth: 3, // Março
    seasonalBonusName: 'Hanami Season',
  },
];

/** `PlantType.fromId`: o que não se reconhece vira lírio, que é o primeiro. */
export function plantaPorId(id: string): Planta {
  return PLANTAS.find((p) => p.id === id) ?? PLANTAS[0];
}

/** `isInSeason`: estamos no mês de bônus desta planta? */
export function naEstacao(planta: Planta, agora: Date = new Date()): boolean {
  if (planta.seasonalBonusMonth == null) return false;
  return agora.getMonth() + 1 === planta.seasonalBonusMonth;
}

/**
 * `effectiveStage`: no mês da planta, uma fase de presente — mas nunca acima
 * de 5, que é onde os desenhos param.
 */
export function faseEfetiva(planta: Planta, base: number, agora: Date = new Date()): number {
  const fase = naEstacao(planta, agora) ? base + 1 : base;
  return Math.min(5, Math.max(0, fase));
}
